import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import AlertPreviewScreen from './AlertPreviewScreen.js';
import './Alert.css';

const ACTIVE_PREVIEW_DEFAULT = {
    title: 'School Engine Alert',
    message: 'Follow the instructions displayed on screen and from staff.',
    backgroundColor: '#111827',
    textColor: '#ffffff'
};

const ENDED_PREVIEW_DEFAULT = {
    title: 'This alert is now over',
    message: 'You may now close this page and await further instruction.',
    backgroundColor: '#065f46',
    textColor: '#ecfdf5'
};

/**
 * Capture the core alert fields prior to wiring the create call. The submission
 * currently logs the payload so wiring can be validated without hitting the API.
 */
export default function AlertCreate() {
    const location = useLocation();

    const severityOptions = useMemo(() => ([
        { value: 'HIGH', label: 'High' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'LOW', label: 'Low' },
    ]), []);

    const typeOptions = useMemo(() => ([
        { value: 'LOCKDOWN', label: 'Lockdown' },
        { value: 'FIRE', label: 'Fire' },
    ]), []);

    const windowOptions = useMemo(() => ([
        { value: true, label: 'Yes - enforce window mode' },
        { value: false, label: 'No - do not enforce' },
    ]), []);

    const [bootstrapData, setBootstrapData] = useState(location.state?.alertBootstrap || null);
    const [bootstrapLoading, setBootstrapLoading] = useState(!location.state?.alertBootstrap);
    const [bootstrapError, setBootstrapError] = useState(false);
    const [audioOptions, setAudioOptions] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const audioPreviewRef = useRef(null);

    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [bgColour, setBgColour] = useState('#f9b233');
    const [textColour, setTextColour] = useState('#706f6f');
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [validUntilTouched, setValidUntilTouched] = useState(false);
    const [audioFile, setAudioFile] = useState(null);
    const [severity, setSeverity] = useState(severityOptions[0]);
    const [type, setType] = useState(typeOptions[0]);
    const [windowEnforced, setWindowEnforced] = useState(windowOptions[1]);

    const populateValidFromNow = useCallback(() => {
        const now = new Date();
        const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        setValidFrom(localIso);
    }, []);

    /**
     * Convert an ISO-like datetime-local value into a +1 hour default.
     * @param {string} fromValue - datetime-local string from the input.
     * @returns {string} datetime-local string for +1 hour.
     */
    const getValidUntilPlusOneHour = useCallback((fromValue) => {
        const parsed = new Date(fromValue);
        if (Number.isNaN(parsed.getTime())) {
            return '';
        }

        const plusOneHour = new Date(parsed.getTime() + 60 * 60 * 1000);
        return new Date(plusOneHour.getTime() - plusOneHour.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
    }, []);

    const activePreview = useMemo(() => ({
        title: (title || '').trim() || ACTIVE_PREVIEW_DEFAULT.title,
        message: (subtitle || '').trim() || ACTIVE_PREVIEW_DEFAULT.message,
        backgroundColor: bgColour || ACTIVE_PREVIEW_DEFAULT.backgroundColor,
        textColor: textColour || ACTIVE_PREVIEW_DEFAULT.textColor
    }), [title, subtitle, bgColour, textColour]);

    const inactivePreview = useMemo(() => ({
        title: (title || '').trim() || ENDED_PREVIEW_DEFAULT.title,
        message: (subtitle || '').trim() || ENDED_PREVIEW_DEFAULT.message,
        backgroundColor: ENDED_PREVIEW_DEFAULT.backgroundColor,
        textColor: ENDED_PREVIEW_DEFAULT.textColor
    }), [title, subtitle]);

    /**
     * Fetch alert bootstrap data (audio files, templates) to hydrate the form.
     */
    const loadBootstrap = useCallback(async () => {
        setBootstrapLoading(true);
        setBootstrapError(false);
        try {
            const response = await SEC_ApiCall('alert/home', {}, setBootstrapError);
            setBootstrapData(response);
        } catch (error) {
            console.error('Failed to load alert bootstrap data', error);
            setBootstrapError(true);
        } finally {
            setBootstrapLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!bootstrapData) {
            loadBootstrap();
        }
    }, [bootstrapData, loadBootstrap]);

    useEffect(() => {
        if (!bootstrapData?.alertData) {
            return;
        }

        const noAudioOption = { value: '', label: 'No Audio' };
        const audioFiles = bootstrapData.alertData.audioFiles || [];
        const audioOptionsParsed = audioFiles
            .filter((file) => file && (file.name || file.url))
            .map((file) => ({
                value: file.url,
                label: file.name || file.url,
            }));

        const mergedAudioOptions = [noAudioOption, ...audioOptionsParsed];

        setAudioOptions(mergedAudioOptions);
        if (!audioFile && mergedAudioOptions.length > 0) {
            setAudioFile(mergedAudioOptions[0]);
        }

        if (Array.isArray(bootstrapData.alertData.templates)) {
            setTemplates(bootstrapData.alertData.templates);
        }
    }, [audioFile, bootstrapData]);

    /**
     * When valid from is set, default valid until to +1 hour unless the user has edited it.
     */
    useEffect(() => {
        if (!validFrom || validUntilTouched) {
            return;
        }

        const nextValidUntil = getValidUntilPlusOneHour(validFrom);
        if (nextValidUntil) {
            setValidUntil(nextValidUntil);
        }
    }, [getValidUntilPlusOneHour, validFrom, validUntilTouched]);

    /**
     * Reset the audio preview when the selected file changes.
     */
    useEffect(() => {
        if (!audioPreviewRef.current) {
            return;
        }

        audioPreviewRef.current.pause();
        audioPreviewRef.current.currentTime = 0;
        setIsAudioPlaying(false);
    }, [audioFile]);

    /**
     * Cleanup the audio preview when the component unmounts.
     */
    useEffect(() => () => {
        if (!audioPreviewRef.current) {
            return;
        }

        audioPreviewRef.current.pause();
        audioPreviewRef.current = null;
    }, []);

    /**
     * Play the currently selected audio file for preview.
     */
    const handleAudioPreview = useCallback(async () => {
        if (!audioFile?.value) {
            toast.error('❌ Please select an audio file to preview.', { position: 'top-right', autoClose: 4000 });
            return;
        }

        if (!audioPreviewRef.current || audioPreviewRef.current.src !== audioFile.value) {
            const nextAudio = new Audio(audioFile.value);

            // Keep the preview button state in sync with native audio playback.
            nextAudio.addEventListener('play', () => setIsAudioPlaying(true));
            nextAudio.addEventListener('pause', () => setIsAudioPlaying(false));
            nextAudio.addEventListener('ended', () => setIsAudioPlaying(false));

            audioPreviewRef.current = nextAudio;
        }

        try {
            if (!audioPreviewRef.current.paused) {
                audioPreviewRef.current.pause();
                audioPreviewRef.current.currentTime = 0;
                setIsAudioPlaying(false);
                return;
            }

            await audioPreviewRef.current.play();
        } catch (error) {
            console.error('Unable to preview alert audio', error);
            setIsAudioPlaying(false);
            toast.error('❌ Unable to play the selected audio file.', { position: 'top-right', autoClose: 4000 });
        }
    }, [audioFile]);

    /**
     * Apply template values to the form when a user selects one.
     * @param {any} template - Template payload from the API.
     */
    const applyTemplate = (template) => {
        if (!template) {
            return;
        }

        setTitle(template.title || template.name || '');
        setSubtitle(template.subtitle || '');
        if (template.bgColour) {
            setBgColour(template.bgColour);
        }
        if (template.textColour) {
            setTextColour(template.textColour);
        }
        if (typeof template.audioFile !== 'undefined') {
            const audioMatch = audioOptions.find((option) => option.value === template.audioFile);
            setAudioFile(audioMatch || { value: template.audioFile || '', label: template.audioFile || 'No Audio' });
        }

        const templateSeverity = (template.severity || '').toUpperCase();
        const severityMatch = severityOptions.find((option) => option.value === templateSeverity);
        setSeverity(severityMatch || severityOptions[0]);

        const templateType = (template.type || '').toUpperCase();
        const typeMatch = typeOptions.find((option) => option.value === templateType);
        setType(typeMatch || typeOptions[0]);

        if (typeof template.windowEnforced === 'boolean') {
            const windowMatch = windowOptions.find((option) => option.value === template.windowEnforced);
            setWindowEnforced(windowMatch || windowOptions[1]);
        }
    };

    /**
     * Build the Dynamo-ready payload and log it for verification.
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        const missingFields = [];
        if (!title.trim()) missingFields.push('Title');
        if (!subtitle.trim()) missingFields.push('Subtitle');
        if (!audioFile) missingFields.push('Audio File');
        if (!severity?.value) missingFields.push('Severity');
        if (!type?.value) missingFields.push('Type');
        if (!validFrom) missingFields.push('Valid From');
        if (!validUntil) missingFields.push('Valid Until');

        if (missingFields.length > 0) {
            toast.error(`⚠️ Please complete: ${missingFields.join(', ')}`, { position: 'top-right', autoClose: 3500 });
            return;
        }

        let validFromIso = '';
        let validUntilIso = '';
        try {
            const parsedFrom = new Date(validFrom);
            if (Number.isNaN(parsedFrom.getTime())) {
                throw new Error('Invalid date');
            }
            validFromIso = parsedFrom.toISOString();
        } catch (error) {
            toast.error('�s���? Please provide a valid date/time for Valid From', { position: 'top-right', autoClose: 3500 });
            return;
        }

        try {
            const parsedDate = new Date(validUntil);
            if (Number.isNaN(parsedDate.getTime())) {
                throw new Error('Invalid date');
            }
            validUntilIso = parsedDate.toISOString();
        } catch (error) {
            toast.error('⚠️ Please provide a valid date/time for Valid Until', { position: 'top-right', autoClose: 3500 });
            return;
        }

        const payload = {
            title: title.trim(),
            subtitle: subtitle.trim(),
            audioFile: audioFile?.value || '',
            severity: severity?.value || '',
            type: type?.value || '',
            validFrom: validFromIso,
            validUntil: validUntilIso,
            bgColour,
            textColour,
            windowEnforced: windowEnforced?.value ?? false,
        };

        setIsSubmitting(true);
        try {
            let requestErrored = false;
            const apiErrorCallback = () => { requestErrored = true; };

            const response = await SEC_DataApiPost('alert/create', JSON.stringify(payload), {}, apiErrorCallback);

            if (requestErrored || !response || response?.message === 'Unauthorized') {
                throw new Error('Alert create failed');
            }

            toast.success('✅ Alert created', { position: 'top-right', autoClose: 3500 });
        } catch (error) {
            console.error('Failed to create alert', error);
            toast.error('❌ Failed to create alert. Please try again.', { position: 'top-right', autoClose: 4000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="alert-form-shell">
            <div className="alert-form-panel">
                <div className="alert-form-header">
                    <div>
                        <p className="alert-form-kicker">New Alert</p>
                        <p className="alert-form-subtitle">
                            Fill in the key details before publishing. Colours and sound help staff
                            quickly identify the alert type. The live previews at the bottom mirror the
                            end-user view so you can check visibility and contrast as you draft.
                        </p>
                    </div>
                </div>

                {bootstrapLoading && (
                    <p className="alert-form-helper">Loading alert options…</p>
                )}
                {!bootstrapLoading && bootstrapError && (
                    <p className="alert-form-error" role="alert">
                        We could not load templates or audio options. You can still draft manually.
                    </p>
                )}

                {templates.length > 0 && (
                    <div className="alert-templates">
                        <div className="alert-templates__header">
                            <p className="alert-form-kicker">Templates</p>
                            <p className="alert-form-helper">
                                Pick a template to pre-fill the alert, then adjust any details.
                            </p>
                        </div>
                        <div className="alert-template-grid">
                            {templates.map((template, index) => {
                                const templateBg = template.bgColour || '#fff9f0';
                                const templateText = template.textColour || '#706f6f';
                                return (
                                <button
                                    key={template.templateId || index}
                                    type="button"
                                    className="alert-template-card"
                                    onClick={() => applyTemplate(template)}
                                    style={{ backgroundColor: templateBg, color: templateText }}
                                >
                                    <span className="alert-template-name">{template.name || template.title}</span>
                                    <span className="alert-template-title">{template.title}</span>
                                    {template.subtitle && (
                                        <span className="alert-template-subtitle">{template.subtitle}</span>
                                    )}
                                </button>
                            );
                            })}
                        </div>
                    </div>
                )}

                <form className="alert-form" onSubmit={handleSubmit}>
                    <div className="alert-form-grid">
                        <label className="alert-form-field">
                            <span className="alert-form-label">Title</span>
                            <input
                                type="text"
                                className="alert-form-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="E.g. Fire Drill - Block B"
                                required
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Subtitle</span>
                            <input
                                type="text"
                                className="alert-form-input"
                                value={subtitle}
                                onChange={(e) => setSubtitle(e.target.value)}
                                placeholder="Short supporting line"
                                required
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Audio File</span>
                            <div className="alert-audio-row">
                                <Select
                                    className="alert-form-select"
                                    classNamePrefix="alert-select"
                                    options={audioOptions}
                                    value={audioFile}
                                    onChange={setAudioFile}
                                    placeholder="Choose an audio cue"
                                />
                                <button
                                    type="button"
                                    className="alert-audio-preview-button"
                                    onClick={handleAudioPreview}
                                    disabled={!audioFile?.value}
                                >
                                    {isAudioPlaying ? 'Stop' : 'Play'}
                                </button>
                            </div>
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Severity</span>
                            <Select
                                className="alert-form-select"
                                classNamePrefix="alert-select"
                                options={severityOptions}
                                value={severity}
                                onChange={setSeverity}
                                placeholder="Select severity"
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Type</span>
                            <Select
                                className="alert-form-select"
                                classNamePrefix="alert-select"
                                options={typeOptions}
                                value={type}
                                onChange={setType}
                                placeholder="Select type"
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Valid From</span>
                            <div className="alert-now-row">
                                <input
                                    type="datetime-local"
                                    className="alert-form-input"
                                    value={validFrom}
                                    onChange={(e) => setValidFrom(e.target.value)}
                                    required
                                />
                                <button type="button" className="alert-now-button" onClick={populateValidFromNow}>
                                    Now
                                </button>
                            </div>
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Valid Until</span>
                            <span className="alert-form-helper alert-form-helper--inline">
                                Defaults to +1 hour after Valid From (you can edit this).
                            </span>
                            <input
                                type="datetime-local"
                                className="alert-form-input"
                                value={validUntil}
                                onChange={(e) => {
                                    setValidUntilTouched(true);
                                    setValidUntil(e.target.value);
                                }}
                                required
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Background Colour</span>
                            <input
                                type="color"
                                className="alert-form-colour"
                                value={bgColour}
                                onChange={(e) => setBgColour(e.target.value)}
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Text Colour</span>
                            <input
                                type="color"
                                className="alert-form-colour"
                                value={textColour}
                                onChange={(e) => setTextColour(e.target.value)}
                            />
                        </label>

                        <label className="alert-form-field">
                            <span className="alert-form-label">Window Enforced</span>
                            <Select
                                className="alert-form-select"
                                classNamePrefix="alert-select"
                                options={windowOptions}
                                value={windowEnforced}
                                onChange={setWindowEnforced}
                                placeholder="Choose enforcement"
                            />
                        </label>
                    </div>

                    <div className="alert-preview-shell">
                        <div className="alert-preview-shell__header">
                            <p className="alert-form-kicker">Live Previews</p>
                        </div>
                        <p className="alert-preview-shell__helper">
                            The live previews below mirror the end-user screen; minor layout differences may occur. Use them to check colours, text visibility, and overall contrast.
                        </p>
                        <div className="alert-live-preview" aria-label="Alert preview">
                            <AlertPreviewScreen
                                variant="active"
                                title={activePreview.title}
                                message={activePreview.message}
                                backgroundColor={activePreview.backgroundColor}
                                textColor={activePreview.textColor}
                            />
                            <AlertPreviewScreen
                                variant="ended"
                                title={inactivePreview.title}
                                message={inactivePreview.message}
                                backgroundColor={inactivePreview.backgroundColor}
                                textColor={inactivePreview.textColor}
                            />
                        </div>
                    </div>

                    <button type="submit" className="alert-create-button alert-form-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting…' : 'Submit Alert'}
                    </button>
                </form>
            </div>
        </div>
    );
}
